import { type FC, useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Card, F0Dialog } from '@factorialco/f0-react'
import { Input, Textarea } from '@factorialco/f0-react/dist/experimental'
import { Calendar, Info, Money, Plane } from '@factorialco/f0-react/icons/app'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  CreateItManagementOrderDocument,
  type DistributorProductVariantsQuery,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { formatPeriod, formatPrice } from 'modules/it_management/lib/formatUtils'
import { buildProductImageUrl } from 'modules/it_management/lib/graphqlProductCatalogContext'
import emptyStateImage from 'modules/it_management/pages/Empty-state-device-catalog-card.png'

type DistributorProductVariant =
  DistributorProductVariantsQuery['itManagement']['distributorProductVariantsConnection']['nodes'][number]

interface RequestDeviceModalProps {
  variant: DistributorProductVariant | null
  onClose: () => void
  onSuccess?: () => void
}

const RequestDeviceModal: FC<RequestDeviceModalProps> = ({ variant, onClose, onSuccess }) => {
  const { email, employee, company, access } = useCurrentSession()
  const { has_it_management_v2_product_catalog_as_service } = useFeatures([
    'it_management_v2_product_catalog_as_service',
  ])

  const [createOrder] = useMutation(CreateItManagementOrderDocument, {
    extractorFn: (data) => data.itManagementMutations.createItmanagementOrder,
  })

  const requestDeviceSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, i18n.t('it_management.device_catalog.request_modal.empty_email'))
          .email(i18n.t('it_management.device_catalog.request_modal.invalid_email')),
        notes: z.string(),
      }),
    []
  )

  type RequestDeviceFormData = z.infer<typeof requestDeviceSchema>

  const deviceName = variant?.product?.name
    ? `${variant.product.brand || ''} ${variant.product.name}`.trim()
    : variant?.sku || variant?.id || ''

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    setValue,
    watch,
    trigger,
  } = useForm<RequestDeviceFormData>({
    resolver: zodResolver(requestDeviceSchema),
    mode: 'onBlur',
    defaultValues: {
      email: email || '',
      notes: '',
    },
  })

  const emailValue = watch('email')
  const notesValue = watch('notes')

  const onSubmit = async (data: RequestDeviceFormData) => {
    if (!data || !company || !variant || !employee) {
      return
    }

    track(trackingEvents.itManagement.deviceCatalogEvents.requestDeviceSubmit, {
      device_id: variant.id,
      device_name: deviceName,
      has_message: data.notes.trim().length > 0,
      company_id: company.id,
      user_id: access.id,
    })

    app.setProgress({
      message: i18n.t('it_management.device_catalog.request_modal.creating_request'),
    })

    try {
      const result = await createOrder({
        variables: {
          companyId: company.id,
          variationId: variant.id,
          distributorOfferId: variant.firstOfferId,
          employeeId: employee.id,
          quantity: 1,
          email: data.email,
          notes: data.notes.trim() || '-',
        },
      })

      app.setProgress(null)

      let errorMessage = 'Unknown backend error'
      const handled = handleResult(result, ({ message }) => {
        errorMessage = message || 'Unknown backend error'
        app.setFlash({
          type: 'wrong',
          message:
            message ||
            i18n.t('it_management.device_catalog.request_modal.failed_to_create_request'),
        })
      })

      if (handled.success) {
        track(trackingEvents.itManagement.deviceCatalogEvents.requestDeviceSuccess, {
          device_id: variant.id,
          company_id: company.id,
          user_id: access.id,
        })

        app.setFlash({
          type: 'correct',
          message: i18n.t('it_management.device_catalog.request_modal.order_created_successfully'),
        })
        onSuccess?.()
        onClose()
      } else {
        track(trackingEvents.itManagement.deviceCatalogEvents.requestDeviceError, {
          device_id: variant.id,
          error_type: 'backend_error',
          error_message: errorMessage || 'Unknown backend error',
          company_id: company.id,
          user_id: access.id,
        })
      }
    } catch (error) {
      app.setProgress(null)

      track(trackingEvents.itManagement.deviceCatalogEvents.requestDeviceError, {
        device_id: variant.id,
        error_type: 'network_error',
        error_message: error instanceof Error ? error.message : 'Unknown error',
        company_id: company.id,
        user_id: access.id,
      })

      app.setFlash({
        type: 'wrong',
        message: i18n.t('it_management.device_catalog.request_modal.failed_to_create_request'),
      })
    }
  }

  const handleClose = useCallback(() => {
    if (variant) {
      track(trackingEvents.itManagement.deviceCatalogEvents.requestDeviceClose, {
        device_id: variant.id,
        company_id: company.id,
        user_id: access.id,
      })
    }
    onClose()
  }, [variant, company.id, access.id, onClose])

  useEffect(() => {
    if (!variant) return
    const style = document.createElement('style')
    style.textContent = `
      [data-radix-portal] > div:has([role="dialog"]) {
        max-width: 820px !important;
        width: 820px !important;
      }
    `
    document.head.appendChild(style)
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style)
      }
    }
  }, [variant])

  if (!variant) return null

  const price = formatPrice(variant.firstOfferCost, variant.firstOfferPeriod)
  const period = formatPeriod(variant.firstOfferPeriod)
  const deliveryInfo =
    variant.deliveryConditions && variant.deliveryConditions !== '-'
      ? variant.deliveryConditions
      : i18n.t('it_management.device_catalog.confirmed_delivery_message')
  const deviceImage = buildProductImageUrl(
    variant.firstProductMediaImageUrl,
    has_it_management_v2_product_catalog_as_service,
    emptyStateImage
  )

  return (
    <F0Dialog
      isOpen
      onClose={handleClose}
      width="lg"
      title={i18n.t('it_management.device_catalog.request_modal.title')}
      primaryAction={{
        label: i18n.t('it_management.device_catalog.request_modal.send_button'),
        onClick: handleSubmit(onSubmit),
        disabled: isSubmitting || !isValid,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: handleClose,
      }}
    >
      <Box flexDirection="row" gap="s24" width="full" paddingTop="s16" paddingBottom="s16">
        <Box style={{ width: '288px' }}>
          <F0Card
            image={deviceImage}
            title={deviceName}
            imageFit="contain"
            imageSize="lg"
            blurredBackground={false}
            metadata={[
              ...(price !== '-'
                ? [
                    {
                      icon: Money,
                      tooltip: i18n.t('it_management.device_catalog.price_title'),
                      property: {
                        type: 'text' as const,
                        value: price,
                      },
                    },
                  ]
                : []),
              ...(period
                ? [
                    {
                      icon: Calendar,
                      tooltip: i18n.t('it_management.device_catalog.period_title'),
                      property: {
                        type: 'text' as const,
                        value: period,
                      },
                    },
                  ]
                : []),
              {
                icon: Plane,
                tooltip: i18n.t('it_management.device_catalog.delivery_title'),
                property: {
                  type: 'text' as const,
                  value: deliveryInfo,
                },
              },
            ]}
          />
        </Box>

        <Box width="s1" background="grey300" />

        <Box gap="s16" flexDirection="column" style={{ width: '440px' }}>
          <Box flexDirection="column" gap="s8">
            <div onBlur={() => trigger('email')}>
              <Input
                label={i18n.t('it_management.device_catalog.request_modal.email_label')}
                type="email"
                required
                value={emailValue}
                onChange={(value) => setValue('email', value)}
                error={errors.email?.message}
              />
            </div>
          </Box>
          <Box gap="s4" flexDirection="column">
            <div onBlur={() => trigger('notes')}>
              <Textarea
                label={i18n.t('it_management.device_catalog.request_modal.notes_label')}
                value={notesValue}
                onChange={(value) => setValue('notes', value)}
                onBlur={() => trigger('notes')}
                error={errors.notes?.message}
              />
            </div>
            <Box style={{ fontSize: '16px' }}>
              <Text size="100" weight="normal" color="grey700">
                {i18n.t('it_management.device_catalog.request_modal.notes_placeholder')}
              </Text>
            </Box>
          </Box>
          <Box
            style={{
              marginTop: '160px',
              backgroundColor: 'var(--color-blue50)',
              border: 'none',
              borderRadius: '8px',
              padding: '12px',
            }}
          >
            <F0Alert
              variant="info"
              title={i18n.t('it_management.device_catalog.request_modal.info_title')}
              description={i18n.t('it_management.device_catalog.request_modal.info_description')}
              icon={Info}
            />
          </Box>
        </Box>
      </Box>
    </F0Dialog>
  )
}

export default RequestDeviceModal
